import React from 'react';
import classNames from 'classnames';
import { focusElementWhenReady } from 'infrastructure/js/utils/domHelper';

require('./editableViewCell.scss');

export default class EditableViewCell extends React.PureComponent {
    constructor(props) {
        super(props);

        this.cellRef = React.createRef();
    }

    componentDidMount() {
        // If this component is part of a multi input cell, register a mousedown event
        // in order to pass the clicked element's index to the editing cell before this
        // component unmounts (single-click-edit swaps this view cell for the editor cell,
        // so the editor needs to know which of its inner inputs to focus).
        if (this.cellRef.current) {
            this.cellRef.current.addEventListener('mousedown', this.handleMouseDown);
        }
    }

    componentWillUnmount() {
        this.cellRef.current?.removeEventListener('mousedown', this.handleMouseDown);
    }

    // Explicitly kick off editing instead of relying on ag-grid's own singleClickEdit
    // timing to pick up a click that landed on one of several stacked inputs within
    // the same ag-grid cell, then focus the specific input that was clicked.
    //
    // Note: we can't rely on the cell editor's afterGuiAttached() grid-api hook to do
    // the focusing (e.g. via a shared "clickedCellIndex" on context) because ag-grid
    // only calls it when the editor's framework component instance is already mounted;
    // for React it's mounted via a portal, and if that mount hasn't committed yet
    // (a real race, since afterGuiAttached is optional and isn't retried), the hook is
    // just silently skipped. Polling for the actual DOM node sidesteps that race.
    handleMouseDown = (e) => {
        const { column, api, rowIndex, index } = this.props;

        // This component is also used standalone (e.g. via ComponentWithErrorCell) for
        // single-value editable columns, where only a bare "value" prop is passed down -
        // none of the ag-grid cell params below are available there. In that case there's
        // no specific sub-row input to target, so just fall back to ag-grid's default
        // click-to-edit handling.
        if (!column || !api || rowIndex === undefined || index === undefined) {
            return;
        }

        e.preventDefault();

        const colKey = column.colId;
        api.startEditingCell({ rowIndex, colKey });
        focusElementWhenReady(colKey + rowIndex + 'index' + index);
    };

    render() {
        if (!this.props.value.enabled) {
            return (
                <div className='input-cell-disabled'>
                    <span>{this.props.value.value}</span>
                </div>
            );
        }
        return (
            <div
                ref={this.cellRef}
                className={classNames(
                    'input-cell',
                    this.props.value.isMulti ? 'multi-row-cell' : '',
                    this.props.value.hasError ? 'has-error' : ''
                )}
            >
                <span>{this.props.value.value}</span>
            </div>
        );
    }
}
